import { type ReactNode } from 'react'

type ButtonVariant = 'filled-grey' | 'filled-white' | 'outline' | 'ghost' | 'text'
type ButtonSize = 'extra-large' | 'large' | 'medium' | 'small' | 'extra-small'

interface ButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  destructive?: boolean
  loading?: boolean
  disabled?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  onClick?: () => void
  children?: ReactNode
  className?: string
  title?: string
}

const SIZE_STYLES: Record<ButtonSize, { height: number; px: number; fontSize: number; lineHeight: string }> = {
  'extra-large': { height: 48, px: 20, fontSize: 16, lineHeight: '24px' },
  'large':       { height: 40, px: 20, fontSize: 12, lineHeight: '16px' },
  'medium':      { height: 38, px: 16, fontSize: 12, lineHeight: '16px' },
  'small':       { height: 32, px: 12, fontSize: 12, lineHeight: '16px' },
  'extra-small': { height: 24, px: 8,  fontSize: 10, lineHeight: '15px' },
}

export function Button({
  variant = 'filled-grey',
  size = 'small',
  destructive = false,
  loading = false,
  disabled = false,
  leadingIcon,
  trailingIcon,
  onClick,
  children,
  className = '',
  title,
}: ButtonProps) {
  const s = SIZE_STYLES[size]

  const base = 'font-jakarta inline-flex items-center justify-center gap-1.5 transition-opacity select-none whitespace-nowrap'

  const variantClass = destructive
    ? 'bg-button-destructive text-white border-0'
    : {
        'filled-grey':  'bg-button-primary text-white border-0',
        'filled-white': 'bg-white text-background-body border-0',
        'outline':      'bg-background-body text-white border border-0.5 border-border-main',
        'ghost':        'bg-transparent text-white border-0 hover:bg-background-body',
        'text':         'bg-transparent text-white border-0 hover:underline',
      }[variant]

  const disabledClass = (disabled || loading) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'

  return (
    <button
      aria-disabled={disabled || loading}
      aria-busy={loading}
      onClick={(!disabled && !loading) ? onClick : undefined}
      title={title}
      className={`${base} ${variantClass} ${disabledClass} ${className}`}
      style={{
        height: s.height,
        paddingLeft: s.px,
        paddingRight: s.px,
        fontSize: s.fontSize,
        lineHeight: s.lineHeight,
      }}
    >
      {loading ? (
        <span
          className="inline-block border-2 border-white border-t-transparent rounded-full animate-spin"
          style={{ width: 12, height: 12 }}
        />
      ) : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  )
}
